const common = {
  app_name: 'LangBook Reader',
};

const translations: Record<string, Record<string, string>> = {
  en: {
    ...common,
    tagline: 'Read books in foreign languages with instant translation',
    get_started: 'Start reading',
    login: 'Sign In with Google',
    logout: 'Sign Out',
    dashboard: 'Dashboard',
    privacy: 'Privacy Policy',
    subscription: 'Subscription',
    subscription_active: 'Active',
    subscription_inactive: 'Inactive',
    subscription_ends: 'Subscription ends',
    admin_panel: 'Admin Panel',
    users: 'Users',
    grant_access: 'Grant Access',
    revoke_access: 'Revoke Access',
    days: 'days',
    search: 'Search...',
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    email: 'Email',
    name: 'Name',
    status: 'Status',
    created_at: 'Created',
    expires: 'Expires',
    cancel: 'Cancel',
    confirm: 'Confirm',
    library: 'Library',
    dictionary: 'Dictionary',
    settings: 'Settings',
    reader: 'Reader',
    flashcards: 'Flashcards',
    save: 'Save',
    translationApi: 'Translation',
    interface_language: 'Interface language',
    landing_login_aria: 'Open login page',
    landing_eyebrow: 'Foreign-language reading, without the tab chaos',
    landing_hero_title: 'Read the book. Learn the language inside it.',
    landing_hero_body:
      'LangBook Reader turns EPUB and TXT books into a focused language lab: tap a word for translation, save it to your dictionary, and review it later with flashcards.',
    landing_secondary_cta: 'See how it works',
    landing_stat_formats_value: 'EPUB/TXT',
    landing_stat_formats_label: 'book import',
    landing_stat_flow_value: '1 tap',
    landing_stat_flow_label: 'to translate',
    landing_stat_languages_value: '5 UI',
    landing_stat_languages_label: 'languages',
    landing_mock_dictionary: 'Dictionary',
    landing_mock_saved_words: 'saved words',
    landing_mock_review: 'Review',
    landing_mock_today: 'today',
    landing_mock_chapter: 'Chapter 8',
    landing_mock_title: 'A quiet page, a faster vocabulary',
    landing_mock_line_1: 'The story keeps moving, even when a new phrase appears.',
    landing_mock_line_2_prefix: 'Tap',
    landing_mock_word: 'wandered',
    landing_mock_line_2_suffix: 'and keep your place in the paragraph.',
    landing_mock_line_3: 'Every saved word becomes part of your personal practice set.',
    landing_mock_translation: 'бродил, wandered',
    landing_features_eyebrow: 'Built for real reading',
    landing_features_title: 'Everything you need between the first page and fluent recall.',
    landing_feature_import_title: 'Import your own books',
    landing_feature_import_body:
      'Add EPUB or TXT files, keep chapters organized, and continue from your saved reading progress.',
    landing_feature_translate_title: 'Translate without leaving the page',
    landing_feature_translate_body:
      'Tap unfamiliar words while reading and save useful translations into your personal dictionary.',
    landing_feature_cards_title: 'Review with flashcards',
    landing_feature_cards_body:
      'Practice saved words with a simple knew/did not know flow that keeps study lightweight.',
    landing_feature_settings_title: 'Tune the reading experience',
    landing_feature_settings_body:
      'Adjust font size, line height, theme, font family, and interface language for long sessions.',
    landing_workflow_eyebrow: 'Workflow',
    landing_workflow_title: 'A cleaner loop for language books.',
    landing_workflow_body:
      'The product is designed around one habit: read first, capture friction only when it matters, then review the words that came from real context.',
    landing_step_import_title: 'Bring a book',
    landing_step_import_body:
      'Import EPUB or TXT, browse your library, sort by date, title, or progress, and open a chapter.',
    landing_step_read_title: 'Tap what slows you down',
    landing_step_read_body:
      'Translation appears in context, so you do not break concentration by jumping to another app.',
    landing_step_review_title: 'Turn saved words into memory',
    landing_step_review_body:
      'Your dictionary becomes flashcards for short review sessions when reading time is over.',
    landing_languages_eyebrow: 'Localized landing',
    landing_languages_title: 'The site speaks the same languages as the product page.',
    landing_languages_body:
      'Switch the landing instantly between English, Russian, German, Spanish, and French. The choice is saved for the next visit.',
    landing_faq_eyebrow: 'Questions',
    landing_faq_title: 'Before you open the reader',
    landing_faq_formats_q: 'Which book formats are supported?',
    landing_faq_formats_a:
      'The app is built around EPUB and TXT import, with chapter navigation and reading progress.',
    landing_faq_login_q: 'Will the login button keep working on GitHub Pages?',
    landing_faq_login_a:
      'Yes. The app uses hash routing, so the main call to action opens the future login route at #/login.',
    landing_faq_language_q: 'Is the reader itself multilingual?',
    landing_faq_language_a:
      'The interface includes English and Russian in the Flutter app, while this landing already supports five web languages.',
    landing_cta_title: 'Open your next foreign-language book with less friction.',
    landing_cta_body:
      'Start with the login page today, then bring the same account into the full reading experience when it is ready.',
  },
  ru: {
    ...common,
    tagline: 'Читайте книги на иностранных языках с мгновенным переводом',
    get_started: 'Начать чтение',
    login: 'Войти через Google',
    logout: 'Выйти',
    dashboard: 'Панель',
    privacy: 'Политика конфиденциальности',
    subscription: 'Подписка',
    subscription_active: 'Активна',
    subscription_inactive: 'Неактивна',
    subscription_ends: 'Подписка до',
    admin_panel: 'Панель администратора',
    users: 'Пользователи',
    grant_access: 'Выдать доступ',
    revoke_access: 'Отозвать доступ',
    days: 'дн.',
    search: 'Поиск...',
    loading: 'Загрузка...',
    error: 'Ошибка',
    retry: 'Повторить',
    email: 'Email',
    name: 'Имя',
    status: 'Статус',
    created_at: 'Создан',
    expires: 'Истекает',
    cancel: 'Отмена',
    confirm: 'Подтвердить',
    library: 'Библиотека',
    dictionary: 'Словарь',
    settings: 'Настройки',
    reader: 'Чтение',
    flashcards: 'Карточки',
    save: 'Сохранить',
    translationApi: 'Перевод',
    interface_language: 'Язык интерфейса',
    landing_login_aria: 'Открыть страницу входа',
    landing_eyebrow: 'Чтение на иностранном без хаоса вкладок',
    landing_hero_title: 'Читайте книгу. Учите язык прямо внутри нее.',
    landing_hero_body:
      'LangBook Reader превращает EPUB и TXT книги в спокойную языковую среду: нажмите на слово, получите перевод, сохраните его в словарь и повторите позже в карточках.',
    landing_secondary_cta: 'Как это работает',
    landing_stat_formats_value: 'EPUB/TXT',
    landing_stat_formats_label: 'импорт книг',
    landing_stat_flow_value: '1 тап',
    landing_stat_flow_label: 'до перевода',
    landing_stat_languages_value: '5 UI',
    landing_stat_languages_label: 'языков',
    landing_mock_dictionary: 'Словарь',
    landing_mock_saved_words: 'слов сохранено',
    landing_mock_review: 'Повторить',
    landing_mock_today: 'сегодня',
    landing_mock_chapter: 'Глава 8',
    landing_mock_title: 'Спокойная страница, быстрый словарь',
    landing_mock_line_1: 'История продолжается, даже если встречается новая фраза.',
    landing_mock_line_2_prefix: 'Нажмите',
    landing_mock_word: 'wandered',
    landing_mock_line_2_suffix: 'и не теряйте место в абзаце.',
    landing_mock_line_3: 'Каждое сохраненное слово становится частью личной практики.',
    landing_mock_translation: 'бродил, wandered',
    landing_features_eyebrow: 'Для настоящего чтения',
    landing_features_title: 'Все нужное от первой страницы до уверенного запоминания.',
    landing_feature_import_title: 'Импортируйте свои книги',
    landing_feature_import_body:
      'Добавляйте EPUB или TXT, сохраняйте главы в порядке и продолжайте с места, где остановились.',
    landing_feature_translate_title: 'Переводите без выхода из текста',
    landing_feature_translate_body:
      'Нажимайте незнакомые слова во время чтения и сохраняйте полезные переводы в личный словарь.',
    landing_feature_cards_title: 'Повторяйте в карточках',
    landing_feature_cards_body:
      'Тренируйте сохраненные слова через простой сценарий «знаю / не знаю» без лишней нагрузки.',
    landing_feature_settings_title: 'Настройте чтение под себя',
    landing_feature_settings_body:
      'Меняйте размер шрифта, межстрочный интервал, тему, гарнитуру и язык интерфейса.',
    landing_workflow_eyebrow: 'Сценарий',
    landing_workflow_title: 'Более чистый цикл для книг на иностранном языке.',
    landing_workflow_body:
      'Продукт построен вокруг одной привычки: сначала читать, сохранять только важные остановки, а потом повторять слова из реального контекста.',
    landing_step_import_title: 'Добавьте книгу',
    landing_step_import_body:
      'Импортируйте EPUB или TXT, смотрите библиотеку, сортируйте по дате, названию или прогрессу и открывайте главу.',
    landing_step_read_title: 'Нажимайте то, что мешает',
    landing_step_read_body:
      'Перевод появляется прямо в контексте, поэтому не нужно выпадать из чтения в другое приложение.',
    landing_step_review_title: 'Превращайте слова в память',
    landing_step_review_body:
      'Ваш словарь становится карточками для коротких повторений после чтения.',
    landing_languages_eyebrow: 'Локализация',
    landing_languages_title: 'Сайт говорит на тех языках, которые заложены на странице.',
    landing_languages_body:
      'Переключайте лендинг между английским, русским, немецким, испанским и французским. Выбор сохранится для следующего визита.',
    landing_faq_eyebrow: 'Вопросы',
    landing_faq_title: 'Перед входом в ридер',
    landing_faq_formats_q: 'Какие форматы книг поддерживаются?',
    landing_faq_formats_a:
      'Приложение рассчитано на импорт EPUB и TXT, навигацию по главам и сохранение прогресса чтения.',
    landing_faq_login_q: 'Кнопка входа будет работать на GitHub Pages?',
    landing_faq_login_a:
      'Да. Приложение использует hash routing, поэтому главный CTA открывает будущий маршрут входа #/login.',
    landing_faq_language_q: 'Сам ридер многоязычный?',
    landing_faq_language_a:
      'Во Flutter-приложении есть английский и русский интерфейсы, а этот лендинг уже поддерживает пять веб-языков.',
    landing_cta_title: 'Откройте следующую книгу на иностранном с меньшим сопротивлением.',
    landing_cta_body:
      'Начните со страницы входа уже сейчас, а затем используйте тот же аккаунт в полном опыте чтения, когда он будет готов.',
  },
  de: {
    ...common,
    tagline: 'Lesen Sie Bücher in Fremdsprachen mit sofortiger Übersetzung',
    get_started: 'Lesen starten',
    login: 'Mit Google anmelden',
    logout: 'Abmelden',
    dashboard: 'Dashboard',
    privacy: 'Datenschutz',
    subscription: 'Abonnement',
    subscription_active: 'Aktiv',
    subscription_inactive: 'Inaktiv',
    subscription_ends: 'Abo endet',
    admin_panel: 'Adminbereich',
    users: 'Benutzer',
    grant_access: 'Zugriff gewähren',
    revoke_access: 'Zugriff entziehen',
    days: 'Tage',
    search: 'Suchen...',
    loading: 'Laden...',
    error: 'Fehler',
    retry: 'Wiederholen',
    email: 'E-Mail',
    name: 'Name',
    status: 'Status',
    created_at: 'Erstellt',
    expires: 'Läuft ab',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    library: 'Bibliothek',
    dictionary: 'Wörterbuch',
    settings: 'Einstellungen',
    reader: 'Leser',
    flashcards: 'Karten',
    save: 'Speichern',
    translationApi: 'Übersetzung',
    interface_language: 'Sprache der Oberfläche',
    landing_login_aria: 'Anmeldeseite öffnen',
    landing_eyebrow: 'Fremdsprachig lesen, ohne Tab-Chaos',
    landing_hero_title: 'Lies das Buch. Lerne die Sprache darin.',
    landing_hero_body:
      'LangBook Reader macht aus EPUB- und TXT-Büchern ein fokussiertes Sprachlabor: Wort antippen, Übersetzung sehen, speichern und später mit Karten wiederholen.',
    landing_secondary_cta: 'Ablauf ansehen',
    landing_stat_formats_value: 'EPUB/TXT',
    landing_stat_formats_label: 'Buchimport',
    landing_stat_flow_value: '1 Tipp',
    landing_stat_flow_label: 'zur Übersetzung',
    landing_stat_languages_value: '5 UI',
    landing_stat_languages_label: 'Sprachen',
    landing_mock_dictionary: 'Wörterbuch',
    landing_mock_saved_words: 'gespeicherte Wörter',
    landing_mock_review: 'Wiederholen',
    landing_mock_today: 'heute',
    landing_mock_chapter: 'Kapitel 8',
    landing_mock_title: 'Ruhige Seite, schneller Wortschatz',
    landing_mock_line_1: 'Die Geschichte läuft weiter, auch wenn eine neue Wendung auftaucht.',
    landing_mock_line_2_prefix: 'Tippe',
    landing_mock_word: 'wandered',
    landing_mock_line_2_suffix: 'und bleibe im Absatz.',
    landing_mock_line_3: 'Jedes gespeicherte Wort wird Teil deiner persönlichen Übung.',
    landing_mock_translation: 'wanderte, umherging',
    landing_features_eyebrow: 'Für echtes Lesen gebaut',
    landing_features_title: 'Alles zwischen erster Seite und sicherem Erinnern.',
    landing_feature_import_title: 'Eigene Bücher importieren',
    landing_feature_import_body:
      'Füge EPUB oder TXT hinzu, halte Kapitel geordnet und lies an deinem gespeicherten Fortschritt weiter.',
    landing_feature_translate_title: 'Übersetzen ohne Seitenwechsel',
    landing_feature_translate_body:
      'Tippe unbekannte Wörter beim Lesen an und speichere nützliche Übersetzungen in deinem Wörterbuch.',
    landing_feature_cards_title: 'Mit Karten wiederholen',
    landing_feature_cards_body:
      'Übe gespeicherte Wörter mit einem leichten wusste / wusste nicht Ablauf.',
    landing_feature_settings_title: 'Lesen fein einstellen',
    landing_feature_settings_body:
      'Passe Schriftgröße, Zeilenhöhe, Theme, Schriftfamilie und Sprache für lange Sitzungen an.',
    landing_workflow_eyebrow: 'Workflow',
    landing_workflow_title: 'Ein klarerer Kreislauf für fremdsprachige Bücher.',
    landing_workflow_body:
      'Das Produkt folgt einer Gewohnheit: erst lesen, Reibung nur bei Bedarf erfassen und Wörter aus echtem Kontext wiederholen.',
    landing_step_import_title: 'Buch mitbringen',
    landing_step_import_body:
      'Importiere EPUB oder TXT, sortiere nach Datum, Titel oder Fortschritt und öffne ein Kapitel.',
    landing_step_read_title: 'Antippen, was bremst',
    landing_step_read_body:
      'Die Übersetzung erscheint im Kontext, ohne in eine andere App zu springen.',
    landing_step_review_title: 'Wörter ins Gedächtnis bringen',
    landing_step_review_body:
      'Dein Wörterbuch wird zu Karten für kurze Wiederholungen nach dem Lesen.',
    landing_languages_eyebrow: 'Lokalisierte Seite',
    landing_languages_title: 'Die Website spricht dieselben Sprachen wie diese Produktseite.',
    landing_languages_body:
      'Wechsle sofort zwischen Englisch, Russisch, Deutsch, Spanisch und Französisch. Die Wahl wird gespeichert.',
    landing_faq_eyebrow: 'Fragen',
    landing_faq_title: 'Bevor du den Reader öffnest',
    landing_faq_formats_q: 'Welche Buchformate werden unterstützt?',
    landing_faq_formats_a:
      'Die App ist für EPUB- und TXT-Import, Kapitelnavigation und Lesefortschritt ausgelegt.',
    landing_faq_login_q: 'Funktioniert der Login-Button auf GitHub Pages?',
    landing_faq_login_a:
      'Ja. Die App nutzt Hash-Routing, daher öffnet der Haupt-CTA die künftige Route #/login.',
    landing_faq_language_q: 'Ist der Reader selbst mehrsprachig?',
    landing_faq_language_a:
      'Die Flutter-App enthält Englisch und Russisch, diese Landingpage unterstützt bereits fünf Websprachen.',
    landing_cta_title: 'Öffne dein nächstes fremdsprachiges Buch mit weniger Reibung.',
    landing_cta_body:
      'Starte heute mit der Login-Seite und nutze später dasselbe Konto im vollständigen Leseerlebnis.',
  },
  es: {
    ...common,
    tagline: 'Lea libros en idiomas extranjeros con traducción instantánea',
    get_started: 'Empezar a leer',
    login: 'Iniciar sesión con Google',
    logout: 'Cerrar sesión',
    dashboard: 'Panel',
    privacy: 'Política de privacidad',
    subscription: 'Suscripción',
    subscription_active: 'Activa',
    subscription_inactive: 'Inactiva',
    subscription_ends: 'La suscripción termina',
    admin_panel: 'Panel de administración',
    users: 'Usuarios',
    grant_access: 'Conceder acceso',
    revoke_access: 'Revocar acceso',
    days: 'días',
    search: 'Buscar...',
    loading: 'Cargando...',
    error: 'Error',
    retry: 'Reintentar',
    email: 'Correo',
    name: 'Nombre',
    status: 'Estado',
    created_at: 'Creado',
    expires: 'Expira',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    library: 'Biblioteca',
    dictionary: 'Diccionario',
    settings: 'Ajustes',
    reader: 'Lectura',
    flashcards: 'Tarjetas',
    save: 'Guardar',
    translationApi: 'Traducción',
    interface_language: 'Idioma de la interfaz',
    landing_login_aria: 'Abrir página de inicio de sesión',
    landing_eyebrow: 'Leer en otro idioma sin caos de pestañas',
    landing_hero_title: 'Lee el libro. Aprende el idioma dentro de él.',
    landing_hero_body:
      'LangBook Reader convierte libros EPUB y TXT en un laboratorio de idiomas enfocado: toca una palabra, mira la traducción, guárdala y repásala después con tarjetas.',
    landing_secondary_cta: 'Ver cómo funciona',
    landing_stat_formats_value: 'EPUB/TXT',
    landing_stat_formats_label: 'importación',
    landing_stat_flow_value: '1 toque',
    landing_stat_flow_label: 'para traducir',
    landing_stat_languages_value: '5 UI',
    landing_stat_languages_label: 'idiomas',
    landing_mock_dictionary: 'Diccionario',
    landing_mock_saved_words: 'palabras guardadas',
    landing_mock_review: 'Repaso',
    landing_mock_today: 'hoy',
    landing_mock_chapter: 'Capítulo 8',
    landing_mock_title: 'Una página tranquila, vocabulario más rápido',
    landing_mock_line_1: 'La historia sigue avanzando aunque aparezca una frase nueva.',
    landing_mock_line_2_prefix: 'Toca',
    landing_mock_word: 'wandered',
    landing_mock_line_2_suffix: 'y conserva tu lugar en el párrafo.',
    landing_mock_line_3: 'Cada palabra guardada entra en tu práctica personal.',
    landing_mock_translation: 'vagó, caminó',
    landing_features_eyebrow: 'Hecho para leer de verdad',
    landing_features_title: 'Todo entre la primera página y recordar con seguridad.',
    landing_feature_import_title: 'Importa tus propios libros',
    landing_feature_import_body:
      'Agrega EPUB o TXT, mantén capítulos ordenados y continúa desde tu progreso guardado.',
    landing_feature_translate_title: 'Traduce sin salir de la página',
    landing_feature_translate_body:
      'Toca palabras desconocidas mientras lees y guarda traducciones útiles en tu diccionario.',
    landing_feature_cards_title: 'Repasa con tarjetas',
    landing_feature_cards_body:
      'Practica palabras guardadas con un flujo simple de sabía / no sabía.',
    landing_feature_settings_title: 'Ajusta la lectura',
    landing_feature_settings_body:
      'Cambia tamaño de fuente, interlineado, tema, tipografía e idioma para sesiones largas.',
    landing_workflow_eyebrow: 'Flujo',
    landing_workflow_title: 'Un ciclo más limpio para libros en otro idioma.',
    landing_workflow_body:
      'El producto se centra en un hábito: leer primero, capturar solo la fricción importante y repasar palabras de contexto real.',
    landing_step_import_title: 'Trae un libro',
    landing_step_import_body:
      'Importa EPUB o TXT, ordena por fecha, título o progreso y abre un capítulo.',
    landing_step_read_title: 'Toca lo que te frena',
    landing_step_read_body:
      'La traducción aparece en contexto, sin romper la concentración saltando a otra app.',
    landing_step_review_title: 'Convierte palabras en memoria',
    landing_step_review_body:
      'Tu diccionario se convierte en tarjetas para repasos cortos al terminar de leer.',
    landing_languages_eyebrow: 'Página localizada',
    landing_languages_title: 'El sitio habla los mismos idiomas que esta página del producto.',
    landing_languages_body:
      'Cambia al instante entre inglés, ruso, alemán, español y francés. La elección se guarda.',
    landing_faq_eyebrow: 'Preguntas',
    landing_faq_title: 'Antes de abrir el lector',
    landing_faq_formats_q: '¿Qué formatos admite?',
    landing_faq_formats_a:
      'La app está pensada para importar EPUB y TXT, navegar capítulos y guardar progreso.',
    landing_faq_login_q: '¿Funcionará el botón de acceso en GitHub Pages?',
    landing_faq_login_a:
      'Sí. La app usa hash routing, por eso el CTA principal abre la futura ruta #/login.',
    landing_faq_language_q: '¿El lector también es multilingüe?',
    landing_faq_language_a:
      'La app Flutter incluye inglés y ruso; esta landing ya soporta cinco idiomas web.',
    landing_cta_title: 'Abre tu próximo libro en otro idioma con menos fricción.',
    landing_cta_body:
      'Empieza hoy con la página de acceso y usa la misma cuenta en la experiencia completa cuando esté lista.',
  },
  fr: {
    ...common,
    tagline: 'Lisez des livres en langues étrangères avec traduction instantanée',
    get_started: 'Commencer à lire',
    login: 'Se connecter avec Google',
    logout: 'Se déconnecter',
    dashboard: 'Tableau de bord',
    privacy: 'Politique de confidentialité',
    subscription: 'Abonnement',
    subscription_active: 'Actif',
    subscription_inactive: 'Inactif',
    subscription_ends: 'Fin de l’abonnement',
    admin_panel: 'Panneau d’administration',
    users: 'Utilisateurs',
    grant_access: 'Accorder l’accès',
    revoke_access: 'Révoquer l’accès',
    days: 'jours',
    search: 'Rechercher...',
    loading: 'Chargement...',
    error: 'Erreur',
    retry: 'Réessayer',
    email: 'E-mail',
    name: 'Nom',
    status: 'Statut',
    created_at: 'Créé',
    expires: 'Expire',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    library: 'Bibliothèque',
    dictionary: 'Dictionnaire',
    settings: 'Réglages',
    reader: 'Lecture',
    flashcards: 'Cartes',
    save: 'Enregistrer',
    translationApi: 'Traduction',
    interface_language: 'Langue de l’interface',
    landing_login_aria: 'Ouvrir la page de connexion',
    landing_eyebrow: 'Lire en langue étrangère sans chaos d’onglets',
    landing_hero_title: 'Lisez le livre. Apprenez la langue dedans.',
    landing_hero_body:
      'LangBook Reader transforme les livres EPUB et TXT en laboratoire linguistique calme : touchez un mot, voyez la traduction, enregistrez-le et révisez-le plus tard avec des cartes.',
    landing_secondary_cta: 'Voir le fonctionnement',
    landing_stat_formats_value: 'EPUB/TXT',
    landing_stat_formats_label: 'import de livres',
    landing_stat_flow_value: '1 geste',
    landing_stat_flow_label: 'pour traduire',
    landing_stat_languages_value: '5 UI',
    landing_stat_languages_label: 'langues',
    landing_mock_dictionary: 'Dictionnaire',
    landing_mock_saved_words: 'mots enregistrés',
    landing_mock_review: 'Révision',
    landing_mock_today: 'aujourd’hui',
    landing_mock_chapter: 'Chapitre 8',
    landing_mock_title: 'Une page calme, un vocabulaire plus rapide',
    landing_mock_line_1: 'L’histoire continue même quand une nouvelle expression apparaît.',
    landing_mock_line_2_prefix: 'Touchez',
    landing_mock_word: 'wandered',
    landing_mock_line_2_suffix: 'et gardez votre place dans le paragraphe.',
    landing_mock_line_3: 'Chaque mot enregistré rejoint votre pratique personnelle.',
    landing_mock_translation: 'errait, wandered',
    landing_features_eyebrow: 'Conçu pour lire vraiment',
    landing_features_title: 'Tout ce qu’il faut entre la première page et le rappel durable.',
    landing_feature_import_title: 'Importez vos livres',
    landing_feature_import_body:
      'Ajoutez EPUB ou TXT, gardez les chapitres organisés et reprenez depuis votre progression.',
    landing_feature_translate_title: 'Traduisez sans quitter la page',
    landing_feature_translate_body:
      'Touchez les mots inconnus pendant la lecture et gardez les traductions utiles dans votre dictionnaire.',
    landing_feature_cards_title: 'Révisez avec des cartes',
    landing_feature_cards_body:
      'Travaillez les mots enregistrés avec un flux simple je savais / je ne savais pas.',
    landing_feature_settings_title: 'Réglez votre confort',
    landing_feature_settings_body:
      'Adaptez taille de police, interligne, thème, famille de police et langue pour lire longtemps.',
    landing_workflow_eyebrow: 'Parcours',
    landing_workflow_title: 'Une boucle plus claire pour les livres en langue étrangère.',
    landing_workflow_body:
      'Le produit suit une habitude : lire d’abord, capturer les frictions importantes, puis revoir les mots issus d’un vrai contexte.',
    landing_step_import_title: 'Ajoutez un livre',
    landing_step_import_body:
      'Importez EPUB ou TXT, triez par date, titre ou progression, puis ouvrez un chapitre.',
    landing_step_read_title: 'Touchez ce qui bloque',
    landing_step_read_body:
      'La traduction apparaît dans le contexte, sans casser la concentration avec une autre application.',
    landing_step_review_title: 'Transformez les mots en mémoire',
    landing_step_review_body:
      'Votre dictionnaire devient des cartes pour de courtes révisions après la lecture.',
    landing_languages_eyebrow: 'Page localisée',
    landing_languages_title: 'Le site parle les mêmes langues que cette page produit.',
    landing_languages_body:
      'Passez instantanément entre anglais, russe, allemand, espagnol et français. Le choix est conservé.',
    landing_faq_eyebrow: 'Questions',
    landing_faq_title: 'Avant d’ouvrir le lecteur',
    landing_faq_formats_q: 'Quels formats sont pris en charge ?',
    landing_faq_formats_a:
      'L’app est conçue pour importer EPUB et TXT, naviguer par chapitre et mémoriser la progression.',
    landing_faq_login_q: 'Le bouton de connexion fonctionnera-t-il sur GitHub Pages ?',
    landing_faq_login_a:
      'Oui. L’app utilise le hash routing, donc le CTA principal ouvre la future route #/login.',
    landing_faq_language_q: 'Le lecteur est-il aussi multilingue ?',
    landing_faq_language_a:
      'L’app Flutter contient l’anglais et le russe, tandis que cette landing prend déjà en charge cinq langues web.',
    landing_cta_title: 'Ouvrez votre prochain livre étranger avec moins de friction.',
    landing_cta_body:
      'Commencez par la page de connexion aujourd’hui, puis utilisez le même compte dans l’expérience complète lorsqu’elle sera prête.',
  },
};

export default translations;
